import { rank, route, type Finding } from '@/shared/ui/council'
import type { Estimator } from '../api'

export type { Finding }

/**
 * The polling council — what this poll's number is allowed to bear, read as decisions.
 *
 * ## Every figure here is served, none is derived
 *
 * The module's central guarantee is that nothing in the polling client computes a
 * percentage: `EstimatorPage` draws a served `(estimate, standardError)` and derives
 * nothing, which is why a chart and the table beside it can never disagree. This file
 * keeps that rule. It reads `separations`, `budget`, `attrition`, `subgroups` and
 * `weighting` off the estimator payload and turns them into sentences — it does not
 * recompute a margin, a gap or a rate, and it must not start.
 *
 * That matters more here than anywhere else in the platform, because the one thing a
 * council over a poll could plausibly get wrong is the margin on a lead. `√(se₁² + se₂²)`
 * is the instinct and it is wrong: the two shares are categories of one multinomial and
 * are negatively correlated, so the server uses `[p₁ + p₂ − (p₁ − p₂)²] / n_eff`. A
 * client-side "improvement" here would quietly overstate every uncertainty on the board.
 *
 * ## What it is for
 *
 * A politician reading a poll asks three questions in this order: am I ahead, can I trust
 * it, and what does it cost to make it better. The findings are ordered so those are the
 * first three cards, and each carries the move rather than only the number.
 */
export function advise(e: Estimator): Finding[] {
  const out: Finding[] = []
  const pct = (n: number | null | undefined, d = 1) =>
    n == null ? '—' : `${(n * 100).toFixed(d)}%`
  const pp = (n: number | null | undefined, d = 1) =>
    n == null ? '—' : `±${(n * 100).toFixed(d)} pts`
  const num = (n: number | null | undefined) =>
    n == null ? '—' : Math.round(n).toLocaleString()

  // ── the lead, and whether it is real ───────────────────────────────────────
  // The first separation is the top two. This is the finding the whole console exists
  // for: an overlap of two intervals is NOT the test, and reading it as one is how a
  // resolved lead gets reported as a statistical tie.
  const top = e.separations[0]
  if (top) {
    const disagrees = top.separated === true && top.intervalsOverlap === true
    out.push({
      id: 'lead',
      severity: top.separated === true ? 'good' : 'serious',
      headline: top.separated === true
        ? `${top.aLabel} leads ${top.bLabel} by ${pct(top.gap)} — and the lead is real.`
        : `${top.aLabel} is ahead of ${top.bLabel} by ${pct(top.gap)}, but this poll cannot resolve it.`,
      evidence: `Gap ${pct(top.gap)} ${pp(top.gapMoe95)}`
        + (top.leadProbability != null
          ? `; probability the lead is genuine ${pct(top.leadProbability, 0)}` : '')
        + (top.intervalsOverlap != null
          ? `. The two options' own 95% intervals ${top.intervalsOverlap ? 'overlap' : 'do not overlap'}.` : '.'),
      action: disagrees
        ? 'Do not let anybody report this as a tie because the error bars touch. Overlapping intervals are the wrong test for a difference between two shares of the same question — the gap has its own, narrower margin, and by that test the lead is separated. This is the single most misread chart in polling.'
        : top.separated === true
          ? 'You may state the lead. State it with the gap margin above, not with the poll’s headline margin of error, which is the margin on a single share and not on a difference.'
          : 'Say "ahead but within the margin", or buy more interviews. Reporting it as a lead is a claim this sample does not support, and it is the claim an opponent will most easily discredit.',
      votesAtStake: 0,
    })
  }

  // ── the design, and what it cost ───────────────────────────────────────────
  if (e.design?.converged === false) {
    out.push({
      id: 'converged',
      severity: 'critical',
      headline: 'The weighting did not converge, so every figure in this poll is withheld.',
      evidence: `Method ${e.design.methodCode ?? 'unknown'}`
        + (e.design.maxDeviation != null ? `, worst deviation ${pct(e.design.maxDeviation)}` : '')
        + `, ${e.design.trimmedCount} weights trimmed.`,
      action: 'Publishing the last iterate of a raking that never settled is publishing a number nobody can defend. Loosen a raking target or drop a dimension the sample cannot support, and run it again.',
      votesAtStake: 0,
    })
  }

  if (e.budget && e.budget.lostInterviews > 0) {
    const b = e.budget
    out.push({
      id: 'budget',
      severity: b.inflation != null && b.inflation > 1.3 ? 'serious' : 'watch',
      headline: `The weighting is costing you the equivalent of ${num(b.lostInterviews)} interviews.`,
      evidence: `Margin ${pp(b.realisedMoe)} against ${pp(b.simpleRandomMoe)} for a simple random sample of the same size`
        + (b.inflation != null ? ` — an inflation of ${b.inflation.toFixed(2)}×` : '')
        + `. Base ${num(b.baseN)}, effective ${num(b.effectiveN)}.`,
      action: 'You paid for the full sample and are getting the precision of a smaller one. The fix is in the field design — quotas closer to the population, or fewer raking dimensions — not in the analysis, where it is already too late.',
      votesAtStake: b.lostInterviews,
    })
  }

  // ── the fieldwork behind the number ────────────────────────────────────────
  const a = e.attrition
  if (a) {
    if (a.responseRate1 != null && a.responseRate1 < 0.4) {
      out.push({
        id: 'response',
        severity: a.responseRate1 < 0.25 ? 'serious' : 'watch',
        headline: `Response rate is ${pct(a.responseRate1)} — most of the people you sampled are not in this poll.`,
        evidence: `${num(a.complete)} complete of ${num(a.eligibleBase)} eligible`
          + (a.cooperationRate1 != null ? `; cooperation ${pct(a.cooperationRate1)}` : '')
          + (a.refusalRate1 != null ? `, refusal ${pct(a.refusalRate1)}` : '') + '.',
        action: 'A low rate is not automatically a biased poll, but it is where bias would hide. Before publishing, check whether refusals cluster in one stratum — a refusal rate that is flat across the frame is far less worrying than one concentrated in a single area or age band.',
        votesAtStake: a.eligibleBase - a.complete,
      })
    }
    if (a.flagged > 0) {
      out.push({
        id: 'flagged',
        severity: a.flagged > a.analysed * 0.1 ? 'serious' : 'watch',
        headline: `${num(a.flagged)} interviews are flagged and excluded from these figures.`,
        evidence: `${num(a.analysed)} interviews analysed. Flagged interviews are held out of the estimates by default.`,
        action: 'Clear the quality queue before publishing. A flag resolved in the poll’s favour adds an interview; one resolved against it is an interviewer problem you would rather find now than after a newspaper has the number.',
        votesAtStake: a.flagged,
      })
    }
    if (a.targetSample > 0 && a.complete < a.targetSample) {
      const short = a.targetSample - a.complete
      out.push({
        id: 'shortfall',
        severity: short > a.targetSample * 0.2 ? 'serious' : 'watch',
        headline: `The poll is ${num(short)} interviews short of its target.`,
        evidence: `${num(a.complete)} complete against a target of ${num(a.targetSample)}.`,
        action: 'Either extend the field period or publish against the smaller base and say so. Quietly treating the achieved sample as the design is how a poll ends up quoting a margin it never earned.',
        votesAtStake: short,
      })
    }
  }

  // ── what you cannot say ────────────────────────────────────────────────────
  const thin = e.subgroups.filter((s) => s.thin)
  if (thin.length > 0) {
    out.push({
      id: 'subgroups',
      severity: 'watch',
      headline: `${thin.length} ${thin.length === 1 ? 'subgroup is' : 'subgroups are'} too thin to report.`,
      evidence: thin.slice(0, 5)
        .map((s) => `${s.dimensionCode}·${s.valueCode} (n=${s.baseN}${s.worstMoe != null ? `, ${pp(s.worstMoe)}` : ''})`)
        .join(' · '),
      action: 'These are the cross-tabs an interviewer, a journalist or an opponent will ask for first, and the honest answer is that this poll cannot support them. If one of these groups matters to the campaign, it needs its own booster sample, not a smaller slice of this one.',
      votesAtStake: 0,
      cellCodes: thin.slice(0, 6).map((s) => `${s.dimensionCode}·${s.valueCode}`),
    })
  }

  const suppressedBands = e.bands.filter((b) => b.suppressed)
  if (suppressedBands.length > 0) {
    out.push({
      id: 'suppressed',
      severity: 'watch',
      headline: `${suppressedBands.length} of ${e.bands.length} options are withheld on this question.`,
      evidence: suppressedBands[0]?.suppressedSentence ?? 'Below the publication threshold.',
      action: 'A withheld cell is not a zero. If these options matter to the read, the question needs a larger base before it can carry them — do not infer them from what is left.',
      votesAtStake: 0,
      cellCodes: suppressedBands.slice(0, 6).map((b) => b.optionLabel),
    })
  }

  // ── where the sample missed the population ─────────────────────────────────
  const skewed = e.weighting
    .filter((w) => w.deviation != null && Math.abs(w.deviation) > 0.05)
    .sort((x, y) => Math.abs(y.deviation!) - Math.abs(x.deviation!))
  if (skewed.length > 0) {
    out.push({
      id: 'weighting',
      severity: Math.abs(skewed[0]!.deviation!) > 0.12 ? 'serious' : 'watch',
      headline: `The field missed the population worst on ${skewed[0]!.dimensionCode} · ${skewed[0]!.valueCode}.`,
      evidence: skewed.slice(0, 4)
        .map((w) => `${w.dimensionCode}·${w.valueCode} ${pct(w.achievedShare)} against ${pct(w.targetShare)} target`)
        .join(' · '),
      action: 'The weights corrected this arithmetically, which is what the design effect above is the price of. Fixing it in the field next wave — a quota, or a different call pattern — buys back precision that no amount of weighting can.',
      votesAtStake: 0,
      cellCodes: skewed.slice(0, 6).map((w) => `${w.dimensionCode}·${w.valueCode}`),
    })
  }

  /*
     The lead is pinned to the front, ahead of the severity ordering.

     When it is separated it carries severity `good`, which would rank it below every
     caveat on the board — and "your lead is real" is the first thing the person reading
     this came for. A finding that is buried because it is good news is a board that only
     ever shows problems, which is how a console gets ignored.
  */
  const lead = out.find((f) => f.id === 'lead')
  const rest = rank(out.filter((f) => f.id !== 'lead'))
  return lead ? [lead, ...rest] : rest
}

const ROUTES: { id: string; words: string[] }[] = [
  { id: 'lead', words: ['lead', 'ahead', 'win', 'winning', 'beat', 'gap', 'margin', 'tie', 'close', 'am i'] },
  { id: 'budget', words: ['design effect', 'deff', 'precision', 'weight', 'cost', 'worth', 'value', 'money', 'efficien'] },
  { id: 'weighting', words: ['weight', 'skew', 'representative', 'population', 'quota', 'miss'] },
  { id: 'response', words: ['response', 'refus', 'rate', 'reach', 'contact', 'answer'] },
  { id: 'flagged', words: ['flag', 'quality', 'fraud', 'cheat', 'suspicious', 'back-check', 'backcheck'] },
  { id: 'shortfall', words: ['sample', 'target', 'short', 'enough', 'how many', 'interviews', 'n ='] },
  { id: 'subgroups', words: ['subgroup', 'crosstab', 'cross-tab', 'young', 'women', 'men', 'segment', 'demograph', 'break'] },
  { id: 'suppressed', words: ['suppress', 'withheld', 'missing', 'blank', 'hidden'] },
  { id: 'converged', words: ['converge', 'raking', 'failed', 'run', 'error'] },
]

/**
 * The instant reading, shown beside the advisor's considered one.
 *
 * This used to BE the answer, and returning "no rule covers that" for anything outside the
 * keyword table read as a refusal rather than as the absence of a request. Now it is the fast
 * half of a pair: null simply means the rules have nothing to add, and the card shows only
 * what the council returns.
 */
export function ask(question: string, findings: Finding[]) {
  return route(question, findings, ROUTES)
}
